/* ================================================================
   JD Quickshop — hover popover for product links
   Companion to jd-product-system.css / jd-quickshop.css

   Attaches a product preview + add-to-cart popover to any link that
   points at /products/... and carries a jd- system class.

   Data comes from sections/jd-quickshop-data.liquid via the Section
   Rendering API, NOT /products/{handle}.js — the Ajax API does not
   expose metafields, and MOQ lives in a metafield.

   Desktop only by design: no touch, no small viewports.
   Configure via window.jdQuickshopConfig before this script loads.
   ================================================================ */

(function () {
  'use strict';

  var CONFIG = Object.assign({
    // Which links get a popover.
    selector: 'a.jd-link[href*="/products/"], a.jd-sw[href*="/products/"], a.jd-part-link[href*="/products/"]',
    // Add this class to any single link that should be skipped.
    skipClass: 'jd-quickshop-skip',
    sectionId: 'jd-quickshop-data',
    minWidth: 1024,      // below this, links behave normally
    prefetchDelay: 80,   // warm the cache this early, before the popover is due
    openDelay: 250,      // hover intent before showing the popover
    skeletonDelay: 200,  // wait this long for data before showing a loading state
    closeDelay: 180,     // grace period to move onto the popover
    gap: 10,             // px between link and popover
    afterAdd: 'stay'     // 'stay' | 'cart' | 'checkout'
  }, window.jdQuickshopConfig || {});

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var cache = new Map();
  var pop = null;
  var anchor = null;
  var product = null;      // loaded product data
  var openToken = 0;       // invalidates in-flight loads when the user moves on
  var openTimer = null;
  var prefetchTimer = null;
  var skeletonTimer = null;
  var closeTimer = null;
  var moqNoticeTimer = null;
  var suppressFocusOpen = false;   // set when Escape hands focus back to the anchor

  /* ─── environment gate ─── */

  function isDesktop() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
           window.innerWidth >= CONFIG.minWidth;
  }

  /* ─── helpers ─── */

  function handleOf(link) {
    var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function variantIdOf(link) {
    var m = (link.getAttribute('href') || '').match(/[?&]variant=(\d+)/);
    return m ? Number(m[1]) : null;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Nothing is shown unless the section returned data we can actually trust.
  function valid(data) {
    if (!data || typeof data !== 'object') return false;
    if (typeof data.title !== 'string' || !data.title) return false;
    if (typeof data.url !== 'string' || data.url.indexOf('/products/') === -1) return false;
    if (!Array.isArray(data.variants) || !data.variants.length) return false;
    return data.variants.every(function (v) {
      return v && typeof v.id === 'number' && v.id > 0 && typeof v.price === 'string';
    });
  }

  function load(handle) {
    if (!cache.has(handle)) {
      var url = ROOT + 'products/' + encodeURIComponent(handle) + '?section_id=' + CONFIG.sectionId;
      cache.set(handle, fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('script[data-jd-quickshop-data]');
          if (!node) throw new Error('no quickshop data in section response');
          var data = JSON.parse(node.textContent);   // throws on a Liquid error page
          if (!valid(data)) throw new Error('quickshop data failed validation');
          return data;
        }));
    }
    return cache.get(handle);
  }

  /* ─── quantity rules ─── */

  function ruleOf(v) {
    return {
      min: Math.max(1, parseInt(v.min, 10) || 1),
      step: Math.max(1, parseInt(v.step, 10) || 1),
      max: v.max ? parseInt(v.max, 10) : null
    };
  }

  // MOQ is a floor; steps run in increments above it.
  function clampQty(value, rule) {
    var n = parseInt(value, 10);
    if (!isFinite(n)) n = rule.min;
    if (n < rule.min) n = rule.min;
    if (rule.step > 1) {
      var steps = Math.round((n - rule.min) / rule.step);
      n = rule.min + Math.max(0, steps) * rule.step;
    }
    if (rule.max && n > rule.max) {
      n = rule.max;
      if (rule.step > 1) {
        n = rule.min + Math.floor((rule.max - rule.min) / rule.step) * rule.step;
      }
    }
    return n;
  }

  // Volume pricing. Tiers when we have them, on their own line under the label,
  // otherwise a plain nudge to the product page where the full table lives.
  function volumeParts(p) {
    if (!p.has_volume) return null;
    if (p.volume_tiers && p.volume_tiers.length) {
      return {
        label: 'Buy more, save more:',
        tiers: p.volume_tiers.map(function (t) {
          return t.min + '+ ' + t.price;
        }).join('  ·  ')
      };
    }
    return { label: 'Volume discounts available', tiers: '' };
  }

  function moreLabel(p) {
    if (!p.show_buy) return 'View product &amp; contact form';
    if (p.has_volume) return 'See full details &amp; volume pricing';
    return 'View full details';
  }

  function moqLabel(p, rule) {
    if (p.moq > 0) {
      return 'Minimum order quantity: ' + p.moq + (p.moq_uom ? ' ' + p.moq_uom : '');
    }
    if (rule.min > 1) {
      return 'Minimum order quantity: ' + rule.min;
    }
    return '';
  }

  /* Marks the live anchor so .jd-sw can hide its own tooltip, and parks the
     native title attribute so the browser tooltip does not double up. */
  function claimAnchor(link) {
    if (anchor === link) return;
    releaseAnchor();
    anchor = link;
    link.classList.add('jd-quickshop-active');
    if (link.hasAttribute('title')) {
      link.dataset.jdQuickshopTitle = link.getAttribute('title');
      link.removeAttribute('title');
    }
  }

  function releaseAnchor() {
    if (!anchor) return;
    anchor.classList.remove('jd-quickshop-active');
    if (anchor.dataset.jdQuickshopTitle != null) {
      anchor.setAttribute('title', anchor.dataset.jdQuickshopTitle);
      delete anchor.dataset.jdQuickshopTitle;
    }
    anchor = null;
  }

  /* ─── popover element ─── */

  function shell() {
    if (pop) return pop;
    pop = document.createElement('div');
    pop.className = 'jd-quickshop';
    pop.setAttribute('role', 'group');
    pop.setAttribute('aria-label', 'Product quick view');
    pop.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    pop.addEventListener('mouseleave', scheduleClose);
    pop.addEventListener('focusin', function () { clearTimeout(closeTimer); });
    pop.addEventListener('focusout', function (e) {
      // Only schedule a close when focus actually leaves the popover.
      if (!pop.contains(e.relatedTarget)) scheduleClose();
    });
    document.body.appendChild(pop);
    return pop;
  }

  function place() {
    if (!pop || !anchor) return;
    var r = anchor.getBoundingClientRect();
    var w = pop.offsetWidth;
    var h = pop.offsetHeight;
    var room = window.innerHeight - r.bottom;
    var above = room < h + CONFIG.gap + 8 && r.top > room;

    var left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
    var top = above ? r.top - h - CONFIG.gap : r.bottom + CONFIG.gap;

    pop.classList.toggle('jd-quickshop--above', above);
    pop.style.left = Math.round(left) + 'px';
    pop.style.top = Math.round(top) + 'px';
    pop.style.setProperty('--jd-quickshop-arrow-x',
      Math.round(Math.min(Math.max(r.left + r.width / 2 - left, 18), w - 18)) + 'px');
  }

  /* ─── rendering ─── */

  function skeleton() {
    return '<div class="jd-quickshop-skeleton">' +
             '<span class="jd-quickshop-sk-img"></span>' +
             '<span class="jd-quickshop-sk-lines"><span></span><span></span><span></span></span>' +
           '</div>';
  }

  function markup(p, v) {
    var rule = ruleOf(v);
    var multi = p.variants.length > 1;
    var image = v.image || p.image;
    var moq = moqLabel(p, rule);
    var volume = volumeParts(p);
    var buyable = p.show_buy && v.available;

    return '<div class="jd-quickshop-inner">' +
        (image
          ? '<a class="jd-quickshop-media" href="' + esc(p.url) + '" tabindex="-1">' +
              '<img src="' + esc(image) + '" alt="' + esc(p.image_alt) + '" loading="lazy"></a>'
          : '') +
        '<div class="jd-quickshop-body">' +
          '<a class="jd-quickshop-title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +

          (p.show_price
            ? '<span class="jd-quickshop-price" data-jd-price>' + esc(v.price) +
                (v.compare_at_price
                  ? '<span class="jd-quickshop-was">' + esc(v.compare_at_price) + '</span>'
                  : '') +
              '</span>'
            : '<span class="jd-quickshop-price jd-quickshop-price--hidden" data-jd-price>Contact for price</span>') +

          '<span class="jd-quickshop-moq' + (moq ? '' : ' jd-quickshop-moq--empty') + '" data-jd-moq>' +
            esc(moq) +
          '</span>' +

          (volume && p.show_price
            ? '<span class="jd-quickshop-volume">' +
                '<span class="jd-quickshop-volume__label">' + esc(volume.label) + '</span>' +
                (volume.tiers
                  ? '<span class="jd-quickshop-volume__tiers">' + esc(volume.tiers) + '</span>'
                  : '') +
              '</span>'
            : '') +

          (multi
            ? '<select class="jd-quickshop-variant" data-jd-variant aria-label="Choose an option">' +
                p.variants.map(function (x) {
                  return '<option value="' + x.id + '"' +
                    (x.id === v.id ? ' selected' : '') +
                    (x.available ? '' : ' disabled') + '>' +
                    esc(x.title) + (x.available ? '' : ' — out of stock') + '</option>';
                }).join('') +
              '</select>'
            : '') +

          (p.show_buy
            ? '<div class="jd-quickshop-actions">' +
                '<div class="jd-quickshop-qty" data-jd-qty-group>' +
                  '<button type="button" class="jd-quickshop-qty__btn" data-jd-step="-1" aria-label="Decrease quantity">&minus;</button>' +
                  '<input class="jd-quickshop-qty__input" data-jd-qty type="number" inputmode="numeric"' +
                    ' min="' + rule.min + '" step="' + rule.step + '"' +
                    (rule.max ? ' max="' + rule.max + '"' : '') +
                    ' value="' + rule.min + '" aria-label="Quantity" autocomplete="off">' +
                  '<button type="button" class="jd-quickshop-qty__btn" data-jd-step="1" aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<button type="button" class="jd-quickshop-add" data-jd-add data-id="' + v.id + '"' +
                  (buyable ? '' : ' disabled') + '>' +
                  (buyable ? 'Add to cart' : 'Unavailable') +
                '</button>' +
              '</div>'
            : '') +

          '<a class="jd-quickshop-more" href="' + esc(p.url) + '">' +
            moreLabel(p) +
            '<span class="jd-quickshop-more__arrow" aria-hidden="true">&rarr;</span>' +
          '</a>' +
          '<span class="jd-quickshop-status" data-jd-status role="status"></span>' +
        '</div>' +
      '</div>';
  }

  function initialVariant(p, wantedId) {
    var byId = wantedId && p.variants.filter(function (v) { return v.id === wantedId; })[0];
    if (byId) return byId;
    return p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
  }

  /* ─── open / close ─── */

  // Data is fetched before anything is shown. A link whose product data is
  // missing, unpublished, or malformed never opens a popover at all — it just
  // behaves like an ordinary link. The loading state only appears if the fetch
  // is slow enough to need one.
  function open(link) {
    var handle = handleOf(link);
    if (!handle) return;

    var wanted = variantIdOf(link);
    var token = ++openToken;
    product = null;

    clearTimeout(skeletonTimer);
    skeletonTimer = setTimeout(function () {
      if (token !== openToken) return;
      claimAnchor(link);
      var el = shell();
      el.innerHTML = skeleton();
      el.classList.add('jd-quickshop--open');
      place();
    }, CONFIG.skeletonDelay);

    load(handle).then(function (data) {
      clearTimeout(skeletonTimer);
      if (token !== openToken) return;
      claimAnchor(link);
      product = data;
      var el = shell();
      el.innerHTML = markup(data, initialVariant(data, wanted));
      el.classList.add('jd-quickshop--open');
      place();
    }).catch(function () {
      clearTimeout(skeletonTimer);
      cache.delete(handle);
      if (token === openToken) close();
    });
  }

  function close() {
    openToken++;                 // any in-flight load is now stale
    clearTimeout(openTimer);
    clearTimeout(prefetchTimer);
    clearTimeout(skeletonTimer);
    clearTimeout(moqNoticeTimer);
    clearTimeout(closeTimer);
    if (pop) pop.classList.remove('jd-quickshop--open');
    releaseAnchor();
    product = null;
  }

  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, CONFIG.closeDelay);
  }

  /* ─── variant + quantity ─── */

  function selectedVariant() {
    if (!product || !pop) return null;
    var sel = pop.querySelector('[data-jd-variant]');
    var addBtn = pop.querySelector('[data-jd-add]');
    var id = sel ? Number(sel.value) : (addBtn ? Number(addBtn.dataset.id) : 0);
    return product.variants.filter(function (v) { return v.id === id; })[0] ||
           product.variants[0];
  }

  function syncVariant() {
    var v = selectedVariant();
    if (!v) return;
    var rule = ruleOf(v);
    var price = pop.querySelector('[data-jd-price]');
    var moqEl = pop.querySelector('[data-jd-moq]');
    var qty = pop.querySelector('[data-jd-qty]');
    var btn = pop.querySelector('[data-jd-add]');

    if (price && product.show_price) {
      price.innerHTML = esc(v.price) +
        (v.compare_at_price ? '<span class="jd-quickshop-was">' + esc(v.compare_at_price) + '</span>' : '');
    }

    if (moqEl) {
      var moq = moqLabel(product, rule);
      moqEl.textContent = moq;
      moqEl.classList.toggle('jd-quickshop-moq--empty', !moq);
    }

    if (qty) {
      qty.min = rule.min;
      qty.step = rule.step;
      if (rule.max) qty.max = rule.max; else qty.removeAttribute('max');
      qty.value = clampQty(qty.value, rule);
    }

    if (btn) {
      btn.dataset.id = v.id;
      btn.disabled = !v.available;
      btn.classList.remove('jd-quickshop-add--done');
      btn.textContent = v.available ? 'Add to cart' : 'Unavailable';
    }
  }

  // Clamps the input and flags whether the MOQ floor was the reason it moved.
  function applyQty(raise) {
    var v = selectedVariant();
    var input = pop && pop.querySelector('[data-jd-qty]');
    if (!v || !input) return null;

    var rule = ruleOf(v);
    var typed = parseInt(input.value, 10);
    var next = clampQty(input.value, rule);
    input.value = next;

    if (raise && isFinite(typed) && typed < rule.min) {
      notice('Minimum order quantity is ' + rule.min +
             (product.moq > 0 && product.moq_uom ? ' ' + product.moq_uom : '') + '.');
    }
    return next;
  }

  function notice(text) {
    var status = pop && pop.querySelector('[data-jd-status]');
    if (!status) return;
    status.textContent = text;
    status.classList.add('jd-quickshop-status--info');
    clearTimeout(moqNoticeTimer);
    moqNoticeTimer = setTimeout(function () {
      if (!status.isConnected) return;
      status.textContent = '';
      status.classList.remove('jd-quickshop-status--info');
    }, 3200);
  }

  /* ─── cart ─── */

  function add(btn) {
    var status = pop.querySelector('[data-jd-status]');
    var qty = applyQty(true);
    if (!qty) return;

    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'   // required for Shopify to enforce inventory
      },
      body: JSON.stringify({ items: [{ id: Number(btn.dataset.id), quantity: qty }] })
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body.description || res.body.message || 'Could not add to cart');

        btn.textContent = 'Added';
        btn.classList.add('jd-quickshop-add--done');

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('jd:cart:added', { bubbles: true, detail: res.body }));

        if (CONFIG.afterAdd === 'cart') { window.location.href = ROOT + 'cart'; return; }
        if (CONFIG.afterAdd === 'checkout') { window.location.href = '/checkout'; return; }

        setTimeout(function () {
          if (!pop || !pop.contains(btn)) return;
          btn.classList.remove('jd-quickshop-add--done');
          btn.disabled = false;
          btn.textContent = 'Add to cart';
        }, 1600);
      })
      .catch(function (err) {
        status.classList.remove('jd-quickshop-status--info');
        status.textContent = err.message;
        btn.disabled = false;
        btn.textContent = 'Add to cart';
      });
  }

  /* ─── wiring ─── */

  function candidate(el) {
    var link = el && el.closest ? el.closest(CONFIG.selector) : null;
    if (!link || link.classList.contains(CONFIG.skipClass)) return null;
    return link;
  }

  document.addEventListener('mouseover', function (e) {
    if (!isDesktop()) return;
    var link = candidate(e.target);
    if (!link) return;
    // Re-entering the current anchor must cancel a pending close, not fall through.
    clearTimeout(closeTimer);
    if (link === anchor) return;
    clearTimeout(openTimer);
    clearTimeout(prefetchTimer);

    // Start the request before the popover is due, so the data is usually
    // already in hand when the hover-intent delay elapses. Errors are swallowed
    // here — open() re-runs the load and handles failure properly.
    prefetchTimer = setTimeout(function () {
      var h = handleOf(link);
      if (h) load(h).catch(function () {});
    }, CONFIG.prefetchDelay);

    openTimer = setTimeout(function () { open(link); }, CONFIG.openDelay);
  });

  document.addEventListener('mouseout', function (e) {
    if (!candidate(e.target)) return;
    clearTimeout(openTimer);
    clearTimeout(prefetchTimer);
    scheduleClose();
  });

  document.addEventListener('focusin', function (e) {
    if (!isDesktop()) return;
    var link = candidate(e.target);
    if (!link || link === anchor) return;
    if (suppressFocusOpen) { suppressFocusOpen = false; return; }
    clearTimeout(closeTimer);
    open(link);
  });

  document.addEventListener('change', function (e) {
    if (!pop) return;
    if (e.target.matches('[data-jd-variant]')) syncVariant();
    else if (e.target.matches('[data-jd-qty]')) applyQty(true);
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var step = e.target.closest('[data-jd-step]');
    if (step && pop && pop.contains(step)) {
      e.preventDefault();
      var v = selectedVariant();
      var input = pop.querySelector('[data-jd-qty]');
      if (!v || !input) return;
      var rule = ruleOf(v);
      var dir = Number(step.dataset.jdStep);
      input.value = clampQty((parseInt(input.value, 10) || rule.min) + dir * rule.step, rule);
      if (dir < 0 && Number(input.value) === rule.min && rule.min > 1) {
        var label = moqLabel(product, rule);
        if (label) notice(label + '.');
      }
      return;
    }

    var btn = e.target.closest('[data-jd-add]');
    if (btn && pop && pop.contains(btn)) { e.preventDefault(); add(btn); }
  });

  /* Keyboard: Tab from the anchor moves into the popover, Escape returns and closes. */

  function focusables() {
    if (!pop) return [];
    return Array.prototype.filter.call(
      pop.querySelectorAll('a[href], button:not([disabled]), select, input'),
      function (el) { return el.offsetParent !== null; }
    );
  }

  document.addEventListener('keydown', function (e) {
    var isOpen = pop && pop.classList.contains('jd-quickshop--open');
    if (!isOpen) return;

    if (e.key === 'Escape') {
      var back = anchor;
      close();  // releaseAnchor() runs inside, so grab the node first
      if (back) { suppressFocusOpen = true; back.focus(); }
      return;
    }

    if (e.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;

    if (e.target === anchor && !e.shiftKey) {
      e.preventDefault();
      items[0].focus();
    } else if (e.target === items[0] && e.shiftKey) {
      e.preventDefault();
      if (anchor) anchor.focus();
    }
  });

  var ticking = false;
  window.addEventListener('scroll', function () {
    if (!anchor || ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      if (!anchor) return;
      var r = anchor.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) close();
      else place();
    });
  }, { passive: true });

  window.addEventListener('resize', close);
})();
